import React, { Suspense, useEffect, useState, useRef, useCallback } from "react";
import "./assets/css/styles.scss";
import "./assets/css/main.css";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Loader from "./components/loader/loader";
import PushNotification from "./components/pushnotification/pushnotification";
import { checkPermission } from "./helpers/permissions";
import { fcmGenerate } from "./components/pushnotification/fcmGenerate";
import { register } from "./serviceWorkerRegistration";
import { SocketContext } from "./context/SocketProvider";

const AdminLayout = React.lazy(() => import("./components/adminLayout"));
const SignIn = React.lazy(() => import("./pages/Authentication/sign-in"));
const SignInUser = React.lazy(() => import("./pages/Authentication/sign-in-user"));
const SignInChecklistUser = React.lazy(() => import("./pages/Authentication/sign-in-checklist"));
const CreateAccountUser = React.lazy(() => import("./pages/Authentication/createAccount"));
const SetPassword = React.lazy(() => import("./pages/Authentication/setPassword"));
const ForgotPassword = React.lazy(() => import("./pages/Authentication/forgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/Authentication/resetPassword"));
const SubmitFeedback = React.lazy(() => import("./pages/Feedback/SubmitFeedback"));
const QRLoaderPage = React.lazy(() => import("./pages/QRCodeGenerator/QRLoaderPage"));
const VenueBooking = React.lazy(() => import("./pages/VenueBooking/VenueBooking"));
const SSOLoaderPage = React.lazy(() => import("./pages/QRCodeGenerator/SSOLoaderPage"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPoilicy/PrivacyPolicy"));
const Subscription = React.lazy(() => import("./pages/Subscription/Subscription"));
const SignInFeedbackUser = React.lazy(() => import("./pages/Authentication/sign-in-feedback"));
const TextingInvite = React.lazy(() => import("./pages/Texting/TextingInvite/textingInvite"));
const SignInTextingUser = React.lazy(() => import("./pages/Authentication/sign-in-texting"));
const PublicOrderPage = React.lazy(() => import("./pages/PublicOrder/PublicOrderPage"));
const PublicMemberServicesPage = React.lazy(() => import("./pages/MemberServicesPublic/PublicMemberServicesPage"));
const LandingPagePublicPage = React.lazy(() => import("./pages/LandingPagePublic/LandingPagePublicPage"));
const CaddyPublicPage = React.lazy(() => import("./pages/CaddiesPublic/CaddyPublicPage"));
const CaddieSchedulePublicPage = React.lazy(() => import("./pages/CaddiesPublic/CaddieSchedulePublicPage"));
const StaffConfirmPage = React.lazy(() => import("./pages/StaffSchedulingPublic/StaffConfirmPage"));

const AppContent = () => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const { socket } = React.useContext(SocketContext);

  const socketRef = useRef(socket);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  const handleSetNotification = useCallback((data) => {
    console.log("App.js: handleSetNotification triggered", data);
    setNotification(data);

    if (data) {
      window.dispatchEvent(new CustomEvent('app:push_notification_received', { detail: data }));

      if (socketRef.current) {
        console.log("App.js: Socket is available, emitting contact_person_list_messaged");
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          socketRef.current.emit("contact_person_list_messaged", {
            company_id: user.company,
            user_id: user.id,
            page: 1,
            page_size: 10,
            search: null,
            campaign_id: null,
            status: null,
            date_from: null,
            date_to: null
          });
          console.log("App.js: Emitted contact_person_list_messaged");
        } else {
          console.warn("App.js: User not found in localStorage, cannot emit");
        }
      } else {
        console.warn("App.js: Socket is null, cannot emit contact_person_list_messaged");
      }
    }
  }, []);

  useEffect(() => {
    let unsubscribe = fcmGenerate(handleSetNotification);

    const handleLogin = () => {
      if (unsubscribe) unsubscribe();
      if (checkPermission("manage_texting")) {
        register();
      }
      unsubscribe = fcmGenerate(handleSetNotification);
      setIsLoggedIn(true);
    };

    const handleRetry = () => {
      if (unsubscribe) unsubscribe();
      unsubscribe = fcmGenerate(handleSetNotification);
    };

    const handleSWMessage = (event) => {
      if (event.data?.type === 'NAVIGATE_TO_CHAT' && event.data?.contact_id) {
        navigate(`/chat-concierge?contact_id=${event.data.contact_id}`);
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSWMessage);
    window.addEventListener("app:user:login", handleLogin);
    window.addEventListener("app:fcm:retry", handleRetry);

    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
      window.removeEventListener("app:user:login", handleLogin);
      window.removeEventListener("app:fcm:retry", handleRetry);
      if (unsubscribe) unsubscribe();
    };
  }, [navigate]);

  const handleNotificationClick = () => {
    if (notification?.contact_id) {
      navigate(`/chat-concierge?contact_id=${notification.contact_id}`);
    }
    setNotification(null);
  };

  const handleDismissNotification = () => {
    setNotification(null);
  };

  return (
    <div>
      <Toaster />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/*" element={<AdminLayout />} />
          <Route exact path="/login" element={<SignIn />} />
          <Route exact path="/login-user/:token/:id" element={<SignInUser />} />
          <Route exact path="/login-checklist-user/:id" element={<SignInChecklistUser />} />
          <Route exact path="/login-feedback-user/:token/:id" element={<SignInFeedbackUser />} />
          <Route exact path="/create-account/:id" element={<CreateAccountUser />} />
          <Route exact path="/reset-password" element={<SetPassword />} />
          <Route exact path="/forgot-password" element={<ForgotPassword />} />
          <Route exact path="/auth/password/:uid/:token" element={<ResetPassword />} />
          <Route path="/submit-feedback/:feedbacktoken" element={<SubmitFeedback />} />
          <Route exact path="/texting-login/:token" element={<SignInTextingUser />} />

          {/* QR Loader */}
          <Route exact path="/qr/:uuid" element={<QRLoaderPage />} />
          {/* QR Loader */}

          {/* Venue Booking */}
          <Route path="/venue/:uuid" element={<VenueBooking />} />
          {/* Venue Booking */}

          {/* SSO */}
          <Route path="/sso/:uuid" element={<SSOLoaderPage />} />
          {/* SSO */}

          {/* Privacy Policy */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* Privacy Policy */}

          {/* Subscription */}
          <Route path="/register" element={<Subscription />} />
          {/* Subscription */}

          {/* Texting Invite */}
          <Route
            exact
            path="/texting-invite/:uid"
            element={<TextingInvite />}
          />
          {/* Texting Invite */}

          {/* Public QR Ordering — anonymous, no admin chrome */}
          <Route path="/order/:public_uuid" element={<PublicOrderPage />} />

          {/* Public Member Services — anonymous, no admin chrome.
              /m/<uuid>            -> chooser (or auto-jump if only 1 type)
              /m/<uuid>/<type>     -> direct to that type's form
              Both honour ?prefill_<field>=<value> for per-ticket QRs. */}
          <Route path="/m/:public_uuid" element={<PublicMemberServicesPage />} />
          <Route
            path="/m/:public_uuid/:type_code"
            element={<PublicMemberServicesPage />}
          />
          {/* Public Landing Page — anonymous chooser of link-out tiles. */}
          <Route path="/l/:public_uuid" element={<LandingPagePublicPage />} />
          {/* Public Caddy review — what a member sees after scanning a caddy's card. */}
          <Route path="/c/:code" element={<CaddyPublicPage />} />
          {/* Public loop confirm — what a caddy sees from their assignment email. */}
          <Route path="/caddie-schedule/:token" element={<CaddieSchedulePublicPage />} />
          {/* Public staff shift confirm — what a staffer sees from their assignment email. */}
          <Route path="/staff-confirm/:token" element={<StaffConfirmPage />} />
        </Routes>
      </Suspense>
      {notification && (
        <PushNotification
          title={notification?.title}
          body={notification?.body}
          onClose={handleDismissNotification}
          onClick={handleNotificationClick}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
